/**
 * VFS (Virtual File System) Service
 *
 * Provides access to Azure Logic Apps VFS endpoints for fetching file
 * listings with metadata. The mtime (modification time) is critical for
 * detecting stale drafts:
 * - If workflow.json mtime > workflow-draft.json mtime, the draft is stale
 * - Stale drafts should be discarded to prevent overwriting newer production changes
 *
 * API Reference:
 * - VFS Listing: GET /admin/vfs/{workflowName}?relativePath=1
 * - VFS File: GET /admin/vfs/{workflowName}/{fileName}?relativePath=1
 */

import type { VFSObject, VFSFileMap } from '@/types/vfs';
import {
  isLocalMode,
  getLocalPluginUrl,
  localFetch,
  LocalFetchError,
} from './localRuntimeClient';
import {
  clearMasterKeyCache,
} from './azureMasterKeyService';
import { AppContext, appFetch, getNewVfsUrl } from './appClient';
import { apiVersions } from './endpoints';

// Re-export for backwards compatibility
export { clearMasterKeyCache };

const MAX_CONCURRENT_FETCHES = 5;

export type VFSFetchOptions = AppContext & {
  workflowName: string;
};

const getFolderUrl = (opts: VFSFetchOptions): string =>
  `${getNewVfsUrl(opts)}/${encodeURIComponent(opts.workflowName)}?relativePath=1&api-version=${apiVersions.otto}`;

const getFileUrl = (opts: VFSFetchOptions, fileName: string): string =>
  `${getNewVfsUrl(opts)}/${encodeURIComponent(opts.workflowName)}/${encodeURIComponent(fileName)}?relativePath=1&api-version=${apiVersions.otto}`;

/**
 * Strict metadata listing used by workflow-list draft detection.
 * A missing folder is expected and returns []; every other failure propagates.
 */
export const fetchFilesFromFolderStrict = async (
  opts: VFSFetchOptions,
  signal?: AbortSignal,
): Promise<VFSObject[]> => {
  if (isLocalMode()) {
    try {
      const localUrl = `${getLocalPluginUrl()}/local/vfs/${encodeURIComponent(opts.workflowName)}`;
      const data = await localFetch<unknown>(localUrl, signal ? { signal } : undefined);
      if (!Array.isArray(data)) {
        throw new Error('Local VFS listing response is not an array');
      }
      return data as VFSObject[];
    } catch (error) {
      if (error instanceof LocalFetchError && error.status === 404) return [];
      throw error;
    }
  }

  const accessToken = await opts.getToken();
  if (!accessToken) throw new Error('Failed to get access token for VFS request');
  const data = await appFetch<unknown>(
    getFolderUrl(opts),
    accessToken,
    signal ? { signal } : undefined,
    [],
  );
  if (!Array.isArray(data)) throw new Error('VFS listing response is not an array');
  return data as VFSObject[];
};

/**
 * Strict JSON read used after metadata identifies a possible unpublished draft.
 * Missing files return null; auth, network, server, and parse failures propagate.
 */
export const fetchFileContentStrict = async <T>(
  opts: VFSFetchOptions,
  fileName: string,
  signal?: AbortSignal,
): Promise<T | null> => {
  if (isLocalMode()) {
    try {
      return await localFetch<T>(
        `${getLocalPluginUrl()}/local/vfs/${encodeURIComponent(opts.workflowName)}/${encodeURIComponent(fileName)}`,
        signal ? { signal } : undefined,
      );
    } catch (error) {
      if (error instanceof LocalFetchError && error.status === 404) return null;
      throw error;
    }
  }

  const accessToken = await opts.getToken();
  if (!accessToken) throw new Error('Failed to get access token for VFS request');
  return appFetch<T | null>(
    getFileUrl(opts, fileName),
    accessToken,
    signal ? { signal } : undefined,
    null,
  );
};

// =============================================================================
// FETCH FILES FROM FOLDER
// =============================================================================

/**
 * Fetches the file listing with metadata from a workflow folder.
 * Returns an array of VFSObject entries with mtime timestamps.
 *
 * @returns Array of file metadata, or empty array on 404 or error (graceful degradation)
 */
export const fetchFilesFromFolder = async (
  opts: VFSFetchOptions
): Promise<VFSObject[]> => {
  if (isLocalMode()) {
    try {
      const localUrl = `${getLocalPluginUrl()}/local/vfs/${opts.workflowName}`;
      return await localFetch<VFSObject[]>(localUrl);
    } catch {
      // Folder doesn't exist or error - return empty array
      return [];
    }
  }

  const vfsUrl = `${getNewVfsUrl(opts)}/${opts.workflowName}?relativePath=1&api-version=${apiVersions.otto}`;

  try {
    const accessToken = await opts.getToken();
    if (!accessToken) {
      console.error('Failed to get access token for VFS request');
      return [];
    }
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };

    const response = await fetch(vfsUrl, { headers, cache: 'no-store' });

    if (response.ok) {
      const data = await response.json();
      // Validate response is an array
      if (!Array.isArray(data)) {
        console.warn('[fetchFilesFromFolder] VFS response is not an array');
        return [];
      }
      return data as VFSObject[];
    }

    // 404 or other error - return empty array (graceful degradation)
    if (response.status !== 404) {
      console.warn(
        `[fetchFilesFromFolder] VFS request failed: ${response.status}`
      );
    }
    return [];
  } catch (error) {
    console.error('[fetchFilesFromFolder] Error fetching VFS listing:', error);
    return [];
  }
};

// =============================================================================
// FETCH FILE CONTENT
// =============================================================================

/**
 * Fetches the content of a single file from a workflow folder.
 *
 * @param opts - Fetch options including workflow name
 * @param fileName - Name of the file to fetch (e.g., "workflow.json")
 * @returns Parsed file content, or null if not found
 */
export const fetchFileContent = async <T>(
  opts: VFSFetchOptions,
  fileName: string
): Promise<T | null> => {
  if (isLocalMode()) {
    try {
      const localUrl = `${getLocalPluginUrl()}/local/vfs/${opts.workflowName}/${fileName}`;
      return await localFetch<T>(localUrl);
    } catch {
      return null;
    }
  }

  const fileUrl = `${getNewVfsUrl(opts)}/${opts.workflowName}/${fileName}?relativePath=1&api-version=${apiVersions.otto}`;

  try {
    const accessToken = await opts.getToken();
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };

    const response = await fetch(fileUrl, { headers, cache: 'no-store' });

    if (response.ok) {
      // Check content type and handle accordingly
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          return (await response.json()) as T;
        } catch (parseError) {
          console.warn(`[fetchFileContent] Failed to parse JSON for ${fileName}:`, parseError);
          return null;
        }
      }
      // For non-JSON files, return null (this function is designed for JSON)
      console.warn(`[fetchFileContent] Non-JSON content type for ${fileName}: ${contentType}`);
      return null;
    }

    if (response.status !== 404) {
      console.warn(`[fetchFileContent] Failed to fetch ${fileName}: ${response.status}`);
    }
    return null;
  } catch (error) {
    console.error(`[fetchFileContent] Error fetching ${fileName}:`, error);
    return null;
  }
};

// =============================================================================
// FETCH WORKFLOW FILES WITH METADATA
// =============================================================================

/**
 * Fetches all files in a workflow folder with their content and metadata.
 * This combines the folder listing (for mtime) with individual file content.
 *
 * Features:
 * - Limits concurrent fetches to avoid overwhelming Azure API
 * - Only fetches JSON files (others are skipped)
 *
 * @returns Map of file names to their content and metadata
 */
export const fetchWorkflowFilesWithMetadata = async (
  opts: VFSFetchOptions
): Promise<VFSFileMap> => {
  // First, get the folder listing with metadata
  const files = await fetchFilesFromFolder(opts);

  if (files.length === 0) {
    return {};
  }

  // Filter to only JSON files to avoid fetching non-parseable files
  const jsonFiles = files.filter((f) => f.name.endsWith('.json'));

  // Fetch content with concurrency limit to avoid overwhelming Azure API
  const result: VFSFileMap = {};

  // Process in batches of MAX_CONCURRENT_FETCHES
  for (let i = 0; i < jsonFiles.length; i += MAX_CONCURRENT_FETCHES) {
    const batch = jsonFiles.slice(i, i + MAX_CONCURRENT_FETCHES);
    const batchPromises = batch.map(async (fileMetadata) => {
      const content = await fetchFileContent<unknown>(opts, fileMetadata.name);
      return {
        name: fileMetadata.name,
        file: {
          data: content,
          metadata: fileMetadata,
        },
      };
    });

    const batchResults = await Promise.all(batchPromises);
    for (const { name, file } of batchResults) {
      result[name] = file;
    }
  }

  return result;
};

// =============================================================================
// TIMESTAMP COMPARISON UTILITIES
// =============================================================================

/**
 * Checks if the first file was modified BEFORE the second file.
 * Used to detect stale drafts:
 * - If workflow-draft.json mtime < workflow.json mtime, the draft is stale
 *
 * Fail-safe behavior: Returns false if timestamps are missing or files don't exist.
 * This prevents accidentally discarding drafts when we can't determine staleness.
 *
 * @param files - VFSFileMap with file metadata
 * @param firstFileName - Name of the first file to compare
 * @param secondFileName - Name of the second file to compare
 * @returns true if first file was modified before second, false otherwise or if uncertain
 */
export const isFileModifiedBefore = (
  files: VFSFileMap,
  firstFileName: string,
  secondFileName: string
): boolean => {
  const firstFile = files[firstFileName];
  const secondFile = files[secondFileName];

  // Fail-safe: if either file or metadata is missing, return false
  // This prevents discarding drafts when we can't determine staleness
  if (!firstFile?.metadata?.mtime || !secondFile?.metadata?.mtime) {
    return false;
  }

  const firstTime = new Date(firstFile.metadata.mtime).getTime();
  const secondTime = new Date(secondFile.metadata.mtime).getTime();

  // Validate timestamps are valid numbers (fail-safe if parsing failed)
  if (isNaN(firstTime) || isNaN(secondTime)) {
    console.warn(
      `[isFileModifiedBefore] Invalid timestamp(s): ${firstFileName}=${firstFile.metadata.mtime}, ${secondFileName}=${secondFile.metadata.mtime}`
    );
    return false;
  }

  // Return true only if first file is strictly older than second
  return firstTime < secondTime;
};

// =============================================================================
// CONTENT EXTRACTION UTILITIES
// =============================================================================

/**
 * Extracts just the content from a VFSFileMap, discarding metadata.
 * Useful when you only need the file contents without timestamps.
 *
 * @param files - VFSFileMap with file content and metadata
 * @returns Map of file names to their content only
 */
export const getFileContentMap = (files: VFSFileMap): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [fileName, file] of Object.entries(files)) {
    result[fileName] = file.data;
  }

  return result;
};
